import { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, TextField, MenuItem, CircularProgress, Alert,
  Chip, Avatar, Stack, Typography, InputAdornment, Tabs, Tab, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Edit as EditIcon, School as SchoolIcon, Search as SearchIcon,
  PersonAdd as PersonAddIcon, EventBusy as LeaverIcon,
  RestoreFromTrash as RestoreIcon, Visibility as ViewIcon,
  EmojiEvents as AlumniIcon, People as PeopleIcon,
  Man as ManIcon, Woman as WomanIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import AdmissionWizard from './AdmissionWizard';
import StudentDetailsDialog from './StudentDetailsDialog';

const TABS = [
  { value: 'active', label: 'Active',  icon: <PeopleIcon  fontSize="small" />, color: 'primary' },
  { value: 'alumni', label: 'Alumni',  icon: <AlumniIcon  fontSize="small" />, color: 'success' },
  { value: 'leaver', label: 'Leavers', icon: <LeaverIcon  fontSize="small" />, color: 'warning' },
  { value: 'all',    label: 'All',     icon: <SchoolIcon  fontSize="small" />, color: 'default' },
];

function GenderTile({ icon, label, total, male, female, tone }) {
  return (
    <Card sx={{ flex: 1, minWidth: 240 }}>
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2,
            bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
            color: `${tone}.main`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{
              textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, fontSize: 10,
            }}>{label}</Typography>
            <Typography variant="h5" fontWeight={800} lineHeight={1.1}>{total}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Chip size="small" icon={<ManIcon sx={{ fontSize: 14 }} />}
            label={`Male ${male}`}
            sx={{ flex: 1, bgcolor: (t) => alpha(t.palette.info.main, 0.08), color: 'info.dark', fontWeight: 700, fontSize: 11 }} />
          <Chip size="small" icon={<WomanIcon sx={{ fontSize: 14 }} />}
            label={`Female ${female}`}
            sx={{ flex: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.08), color: 'error.dark', fontWeight: 700, fontSize: 11 }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AdmissionsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';

  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [counts, setCounts] = useState({
    active: 0, active_male: 0, active_female: 0,
    alumni: 0, alumni_male: 0, alumni_female: 0,
    leaver: 0, leaver_male: 0, leaver_female: 0,
    total: 0,
  });
  const [classes, setCls]   = useState([]);
  const [loading, setL]     = useState(true);
  const [error, setErr]     = useState('');

  const [status, setStatus] = useState('active');
  const [q, setQ]           = useState('');
  const [cla, setCla]       = useState('');
  const [gender, setG]      = useState('');

  const [page, setPage]     = useState(0);
  const [perPage, setPP]    = useState(10);

  const [wizardOpen, setWizard]    = useState(false);
  const [editing, setEditing]      = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);

  // Mark-as-leaver dialog
  const [leaverFor, setLeaverFor] = useState(null);
  const [leaverReason, setLeaverReason] = useState('');
  const [leaverSaving, setLeaverSaving] = useState(false);

  // ── Bulk move to leavers ───────────────────────────────────────
  //
  // Filter to a class, tick who is leaving, move them together. Holds
  // trans_codes rather than rows so a background refresh cannot change
  // what is selected under the operator.
  //
  // Offered on ACTIVE and ALL only: a leaver is already a leaver, and
  // alumni have completed rather than left.
  const [selected, setSelected]   = useState([]);
  const [bulkOpen, setBulkOpen]   = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy]   = useState(false);

  const canBulkMove = status === 'active' || status === 'all';
  // Rows on this page that are eligible — never a leaver row, which the
  // ALL tab mixes in.
  const selectable = canBulkMove
    ? rows.filter(r => r._source !== 'leaver').map(r => r.trans_code)
    : [];

  const toggleOne = (code) => setSelected(s =>
    s.includes(code) ? s.filter(x => x !== code) : [...s, code]);
  const toggleAll = () => setSelected(s =>
    s.length === selectable.length ? [] : [...selectable]);
  const clearSelection = () => setSelected([]);

  // Ask the server what each of them owes before anything moves — a
  // leaver's fee rows stay behind but their child_info row is deleted,
  // so moving somebody mid-debt quietly hides it.
  const openBulk = async () => {
    setBulkOpen(true); setBulkReason(''); setBulkPreview(null);
    try {
      const r = await API.get('/admissions/students/leaver-preview',
        { params: { trans_codes: selected.join(',') } });
      setBulkPreview(r.data);
    } catch (e) {
      setBulkPreview({ error: e.response?.data?.message || 'Could not check balances' });
    }
  };

  const submitBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await API.post('/admissions/students/mark-leaver-bulk', {
        trans_codes: selected,
        reason: bulkReason.trim(),
      });
      swal.success(r.data?.message || 'Moved to leavers');
      setBulkOpen(false); setSelected([]);
      await load();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not move to leavers'));
    } finally { setBulkBusy(false); }
  };

  // List load — runs on filter / page changes. Cancellable so a fast typist
  // doesn't pile up stale responses.
  const loadList = async (signal) => {
    setL(true);
    try {
      const params = { status, page: page + 1, perPage };
      if (q)      params.q      = q;
      if (cla)    params.cla    = cla;
      if (gender) params.gender = gender;
      const list = await API.get('/admissions', { params, signal });
      setRows(list.data.rows || []);
      setTotal(Number(list.data.total || 0));
      setErr('');
    } catch (e) {
      if (e.name === 'CanceledError' || e.name === 'AbortError') return;
      setErr(e.response?.data?.message || 'Load failed');
    } finally { setL(false); }
  };

  // Counts load — only runs once on mount and after a mutation (admit /
  // mark leaver / restore). Filters don't change the totals so we don't
  // refetch on every keystroke.
  const loadCounts = async () => {
    try {
      const cnt = await API.get('/admissions/counts');
      setCounts(cnt.data);
    } catch { /* counts are decorative — silent retry on next mutation */ }
  };

  // Aggregate refresh used by mutations (new / edit / mark / restore).
  const load = async () => { await Promise.all([loadList(), loadCounts()]); };

  useEffect(() => { API.get('/classes').then(r => setCls(r.data)).catch(() => {}); loadCounts(); /* eslint-disable-next-line */ }, []);
  // reset to page 0 when filters change
  useEffect(() => { setPage(0); }, [status, q, cla, gender, perPage]);
  // Drop the selection whenever the list underneath it changes. Keeping
  // ticks across a filter or page change is how the wrong learners get
  // moved — the operator sees three ticked rows and does not realise
  // twenty more are still selected on a page they have left.
  useEffect(() => { setSelected([]); }, [status, q, cla, gender, page, perPage]);
  // Debounced list refetch — 400ms is the sweet spot between typing
  // responsiveness and not bombarding the server. AbortController cancels
  // an in-flight request if the user types again before it resolves.
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => loadList(ctrl.signal), 400);
    return () => { clearTimeout(t); ctrl.abort(); };
    /* eslint-disable-next-line */
  }, [status, q, cla, gender, page, perPage]);

  const openNew = () => { setEditing(null); setWizard(true); };
  const openEdit = async (r) => {
    if (r._source !== 'student') {
      swal.warning('Restore the student to the active register first to edit their record.', 'Cannot edit a leaver');
      return;
    }
    try {
      const res = await API.get(`/admissions/students/${r.trans_code}`);
      setEditing(res.data); setWizard(true);
    } catch { swal.error('Failed to load record'); }
  };

  const submitLeaver = async () => {
    if (!leaverReason.trim()) { swal.warning('Please provide a reason'); return; }
    setLeaverSaving(true);
    try {
      await API.post(`/admissions/students/${leaverFor.trans_code}/mark-leaver`, { reason: leaverReason });
      const name = `${leaverFor.firstname} ${leaverFor.lastname}`;
      setLeaverFor(null); setLeaverReason('');
      swal.success(`${name} moved to leavers`);
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Failed to mark as leaver')); }
    finally { setLeaverSaving(false); }
  };

  const restore = async (r) => {
    const ok = await swal.confirm({
      title: 'Restore student to active register?',
      html: `<strong>${r.firstname} ${r.lastname}</strong> will be moved back from the leavers list ` +
            `into the active register. Their full record (photo, guardian, accounting) will be restored.`,
      confirmText: 'Yes, restore',
      tone: 'success',
      icon: 'question',
    });
    if (!ok) return;
    try {
      await API.post(`/admissions/leavers/${r.trans_code}/restore`);
      swal.success(`${r.firstname} ${r.lastname} restored to active`);
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Restore failed')); }
  };

  const showDetails = (r) => setDetailsFor({ source: r._source, id: r.trans_code });

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <ModuleHeader icon={<SchoolIcon sx={{ fontSize: 28 }} />}
        title="Student Registration"
        subtitle="Register new students and manage active learners, alumni and leavers"
        actionLabel="New Registration" actionIcon={<PersonAddIcon />} onAction={openNew} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <GenderTile icon={<PeopleIcon />} tone="primary" label="Active Students"
          total={counts.active} male={counts.active_male} female={counts.active_female} />
        <GenderTile icon={<AlumniIcon />} tone="success" label="Alumni"
          total={counts.alumni} male={counts.alumni_male} female={counts.alumni_female} />
        <GenderTile icon={<LeaverIcon />} tone="warning" label="Leavers"
          total={counts.leaver} male={counts.leaver_male} female={counts.leaver_female} />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <Tabs value={status} onChange={(_, v) => setStatus(v)} variant="scrollable"
          scrollButtons="auto" sx={{ px: 1, '& .MuiTab-root': { minHeight: 56, fontWeight: 700 } }}>
          {TABS.map(t => (
            <Tab key={t.value} value={t.value}
              icon={t.icon} iconPosition="start"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={700}>{t.label}</Typography>
                  <Chip size="small" label={t.value === 'all' ? counts.total : counts[t.value]}
                    color={status === t.value ? t.color : 'default'}
                    variant={status === t.value ? 'filled' : 'outlined'}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                </Stack>
              } />
          ))}
        </Tabs>
      </Card>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search name or index…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select label="Class" value={cla} onChange={(e) => setCla(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All classes</MenuItem>
            {classes.map(c => <MenuItem key={c.trans_code} value={c.class}>{c.class}</MenuItem>)}
            <MenuItem value="Alumna">Alumni</MenuItem>
          </TextField>
          <TextField select label="Gender" value={gender} onChange={(e) => setG(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

      {/* Appears only once something is ticked, so the page is unchanged
          until the operator actually starts a bulk action. */}
      {canBulkMove && selected.length > 0 && (
        <Card sx={{
          mb: 2, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
          flexWrap: 'wrap',
          bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
          border: '1px solid', borderColor: 'warning.light',
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
            {selected.length} learner{selected.length === 1 ? '' : 's'} selected
            {cla ? ` in ${cla}` : ''}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={clearSelection} sx={{ fontWeight: 700, textTransform: 'none' }}>
            Clear
          </Button>
          <Button size="small" variant="contained" color="warning"
            startIcon={<LeaverIcon fontSize="small" />}
            onClick={openBulk}
            sx={{ fontWeight: 800, textTransform: 'none' }}>
            Move to Leavers
          </Button>
        </Card>
      )}

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                {/* Only on the tabs where a bulk move makes sense.
                    Leavers are already leavers; alumni have finished. */}
                {canBulkMove && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectable.length > 0 && selected.length === selectable.length}
                      indeterminate={selected.length > 0 && selected.length < selectable.length}
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': 'Select all on this page' }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: 56 }}></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Index</TableCell>
                <TableCell>Class</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Guardian</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Mobile</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map(r => {
                  const isLeaver = r._source === 'leaver';
                  const isAlumni = r.calss === 'Alumna';
                  return (
                    <TableRow key={`${r._source}-${r.trans_code}`} hover
                      selected={canBulkMove && selected.includes(r.trans_code)}
                      sx={{ opacity: isLeaver ? 0.75 : 1, '& td': { py: 1 } }}>
                      {canBulkMove && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected.includes(r.trans_code)}
                            onChange={() => toggleOne(r.trans_code)}
                            inputProps={{ 'aria-label': `Select ${r.firstname} ${r.lastname}` }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {r.image ? (
                          <Avatar src={r.image} sx={{ width: 32, height: 32 }} />
                        ) : (
                          <Avatar sx={{
                            width: 32, height: 32, fontSize: 12, fontWeight: 700,
                            bgcolor: isLeaver ? 'warning.main' : isAlumni ? 'success.main' : 'primary.main',
                          }}>
                            {(r.firstname?.[0] || '?').toUpperCase()}
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                          {r.firstname} {r.lastname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                          {r.gender} {r.dob && `· ${r.dob}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.index_no || '—'}
                          sx={{ fontFamily: 'monospace', fontSize: 10.5, height: 20 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {r.calss === 'Alumna' ? 'Alumni' : (r.calss || '—')}
                        </Typography>
                        {r.type && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {r.type}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{r.p_name || '—'}</Typography>
                        {r.p_rele && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {r.p_rele}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {r.p_t_m || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isLeaver ? (
                          <Chip size="small" label="Leaver" color="warning" sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
                        ) : isAlumni ? (
                          <Chip size="small" label="Alumni" color="success" sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
                        ) : (
                          <Chip size="small" label="Active" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => showDetails(r)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!isLeaver && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isSuper && (isLeaver ? (
                          <Tooltip title="Restore to active">
                            <IconButton size="small" color="success" onClick={() => restore(r)}>
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Mark as leaver">
                            <IconButton size="small" color="warning" onClick={() => { setLeaverFor(r); setLeaverReason(''); }}>
                              <LeaverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={canBulkMove ? 9 : 8} align="center" sx={{ py: 6 }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                    <Typography color="text.disabled">No students in this view</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => { setPP(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:"
          showFirstButton
          showLastButton
        />
      </Card>

      <AdmissionWizard
        open={wizardOpen}
        onClose={() => setWizard(false)}
        onSaved={load}
        editing={editing}
      />

      <StudentDetailsDialog
        open={!!detailsFor}
        onClose={() => setDetailsFor(null)}
        source={detailsFor?.source}
        transCode={detailsFor?.id}
      />

      {/* ── Bulk move to leavers ──────────────────────────────────
          Shows exactly who is going and what each of them still owes,
          because the move deletes their child_info row: their fee rows
          survive but nothing that joins on child_info will find them
          again, so an unpaid balance effectively disappears from the
          reports. Better to see it here than discover it at year end. */}
      <Dialog open={bulkOpen} onClose={() => !bulkBusy && setBulkOpen(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.warning.dark} 0%, ${t.palette.warning.main} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <LeaverIcon />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.2 }}>
              Move {selected.length} learner{selected.length === 1 ? '' : 's'} to Leavers
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
              {cla ? cla : 'Mixed classes'} · this removes them from the active register
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {!bulkPreview ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : bulkPreview.error ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{bulkPreview.error}</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Nothing is deleted. Each learner keeps their full record and every balance —
                they are marked as having left, so they stop being billed and stop appearing
                on registers and class lists. You can restore them at any time.
              </Alert>
              {bulkPreview.total_owing > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>
                    GHS {Number(bulkPreview.total_owing).toFixed(2)} is still owed by{' '}
                    {bulkPreview.students.filter(s => s.balance > 0.005).length} of them.
                  </Typography>
                  <Typography sx={{ fontSize: 12.5 }}>
                    That debt stays on record and stays collectable — but they will no longer
                    be billed for new terms.
                  </Typography>
                </Alert>
              )}
              {bulkPreview.total_credit < -0.005 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  GHS {Math.abs(Number(bulkPreview.total_credit)).toFixed(2)} is in credit —
                  the school owes that back. Settle it before they go.
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 260, mb: 2, border: '1px solid',
                borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Index</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {(bulkPreview.students || []).map(s => (
                      <TableRow key={s.trans_code}>
                        <TableCell sx={{ fontSize: 12.5 }}>{s.name}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{s.index_no}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{s.cla}</TableCell>
                        <TableCell align="right" sx={{
                          fontSize: 12.5, fontWeight: 700,
                          color: s.balance > 0.005 ? 'error.main'
                            : s.balance < -0.005 ? 'success.dark' : 'text.secondary',
                        }}>
                          {Number(s.balance).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <TextField
            label="Reason" required fullWidth multiline minRows={2}
            placeholder="e.g. Transferred to another school"
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            helperText={bulkReason.trim().length < 5
              ? 'At least 5 characters — this is stored against every learner moved.'
              : `Recorded against all ${selected.length}.`}
            error={bulkReason.length > 0 && bulkReason.trim().length < 5}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75 }}>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkBusy}
            sx={{ fontWeight: 700, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="warning"
            onClick={submitBulk}
            disabled={bulkBusy || bulkReason.trim().length < 5 || !selected.length}
            startIcon={bulkBusy ? <CircularProgress size={16} color="inherit" /> : <LeaverIcon />}
            sx={{ fontWeight: 800, textTransform: 'none', minWidth: 190 }}>
            {bulkBusy ? 'Moving…' : `Move ${selected.length} to Leavers`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!leaverFor} onClose={() => !leaverSaving && setLeaverFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <LeaverIcon /> Mark as Leaver
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" mb={2}>
            Move <strong>{leaverFor?.firstname} {leaverFor?.lastname}</strong> from the active register to the leavers register.
            Their full record (including photo, guardian, health and accounting) is preserved and can be restored later.
          </Typography>
          <TextField fullWidth multiline rows={3} label="Reason" autoFocus
            value={leaverReason} onChange={(e) => setLeaverReason(e.target.value)}
            placeholder="e.g. Family relocated, transferred to another school, dropped out…" />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setLeaverFor(null)} disabled={leaverSaving}>Cancel</Button>
          <Button variant="contained" color="warning" startIcon={<LeaverIcon />}
            onClick={submitLeaver} disabled={leaverSaving || !leaverReason.trim()}>
            {leaverSaving ? 'Moving…' : 'Move to Leavers'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
